// `IssueTimeline` — Phase 2d. Vertical mini-timeline rendered below the
// AC field in the editor when an existing issue is open. Each row shows
// the event time, an icon hinting the kind, and the human-readable text.

import type { JSX } from "react";

import type { IssueTimelineEvent } from "@loom/shared/dashboard";

import { useT, type Translator } from "../../i18n/index.js";
import { useRelativeTime } from "../../i18n/format.js";
import { STAGE_LABEL_KEY } from "../stage";

import { Icon, type IconName } from "./Icon";

export interface IssueTimelineProps {
  readonly events: ReadonlyArray<IssueTimelineEvent>;
  readonly loading: boolean;
}

const iconForKind = (kind: IssueTimelineEvent["kind"]): IconName => {
  switch (kind) {
    case "stage_in":
      return "chevron-up";
    case "stage_out":
      return "chevron-down";
    case "label_add":
      return "plus";
    case "label_remove":
      return "x";
  }
};

const labelFor = (event: IssueTimelineEvent, t: Translator): string => {
  if (event.kind === "stage_in" && event.stage) {
    return t("dashboard.issueTimeline.entered", { stage: t(STAGE_LABEL_KEY[event.stage]) });
  }
  if (event.kind === "stage_out" && event.stage) {
    return t("dashboard.issueTimeline.left", { stage: t(STAGE_LABEL_KEY[event.stage]) });
  }
  if (event.kind === "label_add") return t("dashboard.issueTimeline.added", { label: event.label });
  return t("dashboard.issueTimeline.removed", { label: event.label });
};

export const IssueTimeline = ({
  events,
  loading,
}: IssueTimelineProps): JSX.Element => {
  const t = useT();
  const rel = useRelativeTime();
  const formatTime = (iso: string): string => {
    const ms = Date.parse(iso);
    if (!Number.isFinite(ms)) return iso;
    return rel(ms);
  };
  if (loading) {
    return (
      <div className="issue-timeline">
        <div className="issue-timeline-empty">{t("dashboard.issueTimeline.loading")}</div>
      </div>
    );
  }
  if (events.length === 0) {
    return (
      <div className="issue-timeline">
        <div className="issue-timeline-empty">{t("dashboard.issueTimeline.empty")}</div>
      </div>
    );
  }
  // Show most-recent first; events arrive oldest → newest from GitLab.
  const ordered = [...events].reverse();
  return (
    <div className="issue-timeline">
      {ordered.map((event, idx) => (
        <div key={`${event.at}-${event.label}-${idx}`} className={`issue-timeline-row ${event.kind}`}>
          <span className="issue-timeline-time">{formatTime(event.at)}</span>
          <span className="issue-timeline-icon">
            <Icon name={iconForKind(event.kind)} size={11} />
          </span>
          <span className="issue-timeline-text">{labelFor(event, t)}</span>
          {event.actor && (
            <span className="issue-timeline-actor">@{event.actor}</span>
          )}
        </div>
      ))}
    </div>
  );
};
